import "react-day-picker/lib/style.css";
import moment from "moment-timezone";
import React from "react";
import {
  DayModifiers,
  DayPickerProps,
  LocaleUtils
} from "react-day-picker";
import styled, { css } from "styled-components";
import * as _ from "lodash";

import {
  isDisabledReason,
  isWarning,
  hasOrders,
  media,
  formatDate,
  isSameDay
} from "src/library/utils";
import { useCurrentUser } from "src/core/users/hooks";

import { Button } from "../Button";
import YearMonthSelect from "./YearMonthSelect";
import { DayPicker } from "./DayPicker";
import { LoadingSpinner } from "../LoadingSpinner";
import { Text } from "../Text";

interface OrderDate { id: number; date: Date }
export type OrderDates = OrderDate[];

interface DisabledDateInfo {
  isDisabled: boolean;
  component?: JSX.Element;
}

interface WarningDateInfo {
  isWarning: boolean;
  component?: JSX.Element;
}

export interface IOrderDayPickerProps {
  theme?: "light" | "dark";
  handleDateChange: (date: Date) => void;
  handleMonthChange?: (date: Date) => void;
  date: Date;
  disabledDates?: ComponentDates[];
  warningDates?: ComponentDates[];
  activeDates?: ComponentDates[];
  activeDate?: Date;
  fromDate: Date;
  toDate: Date;
  disabledDays?: number[];
  disabled?: (date: Date) => DisabledDateInfo;
  warning?: (date: Date) => WarningDateInfo;
  orderDates?: OrderDates;
  singleDailyDelivery?: boolean;
  withOrdersComponent?: any;
  handleRouteToOrder?: (date: Date) => void;
  modifiersStyles?: DayPickerProps["modifiersStyles"];
  loading?: boolean;
  generalWarning?: string;
}

export interface ComponentDates {
  component?: JSX.Element;
  date: Date;
}

export const OrderDayPicker: React.ReactFCWithChildren<IOrderDayPickerProps> = React.memo(props => {
  const {
    disabledDates, orderDates, singleDailyDelivery, handleMonthChange, loading
  } = props;

  const [ { currentRole } ] = useCurrentUser();
  const [ activeDate, setActiveDate ] = React.useState<Date | undefined>(props.activeDate);
  const [ month, setMonth ] = React.useState<Date>();
  const [ activeWarning, setActiveWarning ] = React.useState<JSX.Element[] | null>();
  const disabledDays = props.disabledDays || [];
  const warningDates = props.warningDates || [];

  const disabledInfo = (day: Date): DisabledDateInfo => {
    const disabledReason = isDisabledReason(day, disabledDates);

    if (disabledReason) {
      return {
        isDisabled: true,
        component: disabledReason
      };
    } else if (day > props.toDate) {
      return {
        isDisabled: true,
        component: (
          <Text>
            Limit date to create an order is
            {" "}

            {formatDate(props.toDate)}
            .
          </Text>
        )
      };
    } else if (moment.utc(day).format("YYYY-MM-DD") < moment.utc().format("YYYY-MM-DD")) {
      return {
        isDisabled: true,
        component: (
          <Text>
            Orders cannot be created in a past date.
          </Text>
        )
      };
    } else if (disabledDays.includes(moment.utc(day).isoWeekday())) {
      return {
        isDisabled: true,
        component: (
          <Text>
            {moment.utc(day).format("dddd")}

            {" "}
            is not available for deliveries.
          </Text>
        )
      };
    } else if (props.disabled) {
      return props.disabled(day);
    } else {
      return { isDisabled: false };
    }
  };

  const warningInfo = (day: Date): WarningDateInfo => {
    const warningReason = isWarning(day, warningDates);

    if (warningReason) {
      return {
        isWarning: true,
        component: _.find(warningDates, warningDate => isSameDay(warningDate.date, day))?.component
      };
    } else if (props.warning) {
      return props.warning(day);
    } else {
      return { isWarning: false };
    }
  };

  const modifiers = {
    disabled: (day: Date) => disabledInfo(day).isDisabled,
    warning: (day: Date) => warningInfo(day)?.isWarning,
    withOrders: (day: Date) => hasOrders(day, orderDates ? orderDates : undefined)
  };

  const fromMonth = props.fromDate;
  const toMonth = props.toDate;

  const handleDayClick = (day: Date, modifiers?: DayModifiers) => {
    const warnings: JSX.Element[] = [];

    if (modifiers && modifiers.disabled) {
      warnings.push(disabledInfo(day).component as JSX.Element);
      setActiveDate(undefined);
    } else if (modifiers && modifiers.warning) {
      warnings.push(warningInfo(day).component as JSX.Element);
    } else if (modifiers && modifiers.withOrders && props.withOrdersComponent && props.handleRouteToOrder) {
      warnings.push(
        <>
          <Button
            $buttonType="secondary"
            onClick={() => props.handleRouteToOrder && props.handleRouteToOrder(day)}
          >
            <>
              View this order
            </>
          </Button>
        </>
      );
    } else if (modifiers && modifiers.withOrders) {
      if (singleDailyDelivery) {
        warnings.push(
          <div>
            This day has an order already.
          </div>
        );
      } else {
        const matchingOrders = orderDates ? orderDates.filter(dateOrder => isSameDay(dateOrder.date, day)) : [];
        const urlRoute = `${currentRole?.type === "admin" && matchingOrders.length > 1 ? "/admin/" : "/"}orders`;

        if (matchingOrders.length > 0) {
          let Action: JSX.Element | undefined;

          // if only one order, direct to the order
          if (matchingOrders.length === 1) {
            Action = (
              <span
                onClick={() => window.open(`${urlRoute}/${matchingOrders[ 0 ].id}`, "_blank")}
                className="link-text"
              >
                View Order.
              </span>
            );
          }

          // if more than one order, direct to the orders pages with filter applied
          if (matchingOrders.length > 1) {
            Action = (
              <span
                onClick={() => window.open(`${urlRoute}?filters=deliveryDate:${moment.utc(matchingOrders[ 0 ].date).format("YYYY-MM-DD")}`, "_blank")}
                className="link-text"
              >
                View Orders.
              </span>
            );
          }

          warnings.push(
            <div className="order-exists-multi-daily-delivery">
              <Text className="warning">
                {`This day has ${matchingOrders.length === 1 ? "an order" : "multiple orders"} already. `}

                {"By clicking Confirm, you will create another order for this day. Click here to "}

                {Action && (Action)}
              </Text>
            </div>
          );
        }
      }

      setActiveDate(undefined);
    } else {
      setActiveWarning(null);
    }

    if (warnings.length > 0) {
      setActiveWarning(warnings);
    }

    setActiveDate(day);
    props.handleDateChange(day);
  };

  const setMonthAfterChange = React.useCallback((month: Date) => {
    setMonth(month);

    if (handleMonthChange) {
      handleMonthChange(month);
    }
  }, [ handleMonthChange ]);

  return (
    <Wrapper className="calendar-wrapper">
      <DayPicker
        className={props.theme || "light"}
        initialMonth={props.date}
        month={month}
        firstDayOfWeek={1}
        numberOfMonths={1}
        fromMonth={props.fromDate}
        toMonth={props.toDate}
        selectedDays={activeDate}
        modifiers={modifiers}
        modifiersStyles={props.modifiersStyles}
        onDayClick={(day, dayModifiers) => handleDayClick(day, dayModifiers)}
        onMonthChange={month => setMonthAfterChange(month)}
        captionElement={({ date, localeUtils }: {date: Date; localeUtils: LocaleUtils}) => (
          <div className="spinner-caption">
            <YearMonthSelect
              date={date}
              localeUtils={localeUtils}
              onChange={month => setMonthAfterChange(month)}
              fromMonth={fromMonth}
              toMonth={toMonth}
            />

            {loading && (
              <LoadingSpinner
                className="loader-spinner"
                type="circular-pulse"
                position="inline"
              />
            )}
          </div>
        )}
      />

      {activeWarning ? (
        <WarningComponent>
          {activeWarning}
        </WarningComponent>
      ) : (
        <>
          {props.generalWarning && (
            <WarningComponent>
              <Text>
                {props.generalWarning}
              </Text>
            </WarningComponent>
          )}
        </>
      )}
    </Wrapper>
  );
});

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    min-width: 300px;

    .spinner-caption {
      display: table-caption;

      .DayPicker-Caption,
      .loader-spinner {
        display: inline-block;
      }

      .loader-spinner {
        margin-left: ${theme.spacing.margins.md};
      }
    }

    ${media({ lt: theme.breakpoints.sm })} {
      width: 100%;
    }
  `}
`;

const WarningComponent = styled.div`
  ${({ theme }) => css`
    border-top: 1px solid ${theme.colors.lightGrey};
    padding: ${theme.spacing.padding.md};

    .disabled, .disabled div {
      color: ${theme.colors.error};
    }

    .warning {
      color: ${theme.colors.warn};
    }

    .order-exists-multi-daily-delivery {
      text-align: center;
      width: 100%;

      .link-text {
        cursor: pointer;
        color: #0077be;
        text-decoration: underline;
      }
    }
  `}
`;
